import type { CrmRatesOnFile } from '@discovr/features';

/** Below this a non-zero share would round to 0.00%, which reads as "none". */
const SLIVER_SHARE = 0.0001;
const SLIVER_LABEL = '<0.01%';
const UNKNOWN = '—';
const PERCENT_DECIMALS = 2;
const HUNDREDTHS_PER_WHOLE = 10_000;

/** A 0–100 percentage at exactly 2 decimals, trailing zeros kept: "46.25%", "2.00%". */
export function formatPercentValue(percent: number): string {
  return `${percent.toFixed(PERCENT_DECIMALS)}%`;
}

/** `part` of `whole` as a 2-decimal percentage: "—" when the whole is unknown or zero, "<0.01%" for a non-zero sliver. */
export function formatShare(part: number, whole: number | null | undefined): string {
  if (whole == null || whole <= 0) return UNKNOWN;
  const share = part / whole;
  if (share > 0 && share < SLIVER_SHARE) return SLIVER_LABEL;
  return formatPercentValue(share * 100);
}

/** A 0–1 rate as a 2-decimal percentage, "—" when the backend has nothing to measure yet. */
export function formatRate(rate: number | null | undefined): string {
  if (rate == null) return UNKNOWN;
  return formatShare(rate, 1);
}

/** A non-zero count whose share rounds to 0.00% still reads "<0.01%", never "none". */
export function formatSplitPercent(percent: number, count: number): string {
  return count > 0 && percent === 0 ? SLIVER_LABEL : formatPercentValue(percent);
}

/**
 * Percentages at 2 decimals that always add up to exactly 100 (largest
 * remainder over hundredths), so a stage legend never reads 99.99% or
 * 100.01%. All zeros when the total is zero.
 */
export function splitPercents(counts: readonly number[]): number[] {
  const total = counts.reduce((sum, n) => sum + n, 0);
  if (total <= 0) return counts.map(() => 0);
  const exact = counts.map((n) => (n * HUNDREDTHS_PER_WHOLE) / total);
  const result = exact.map((value) => Math.floor(value));
  const shortfall = HUNDREDTHS_PER_WHOLE - result.reduce((sum, n) => sum + n, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  byRemainder.slice(0, shortfall).forEach(({ index }) => {
    result[index] = (result[index] ?? 0) + 1;
  });
  return result.map((hundredths) => hundredths / 100);
}

const TIME_OF_DAY = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
const DAY_MONTH = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function previousLocalDay(day: Date): Date {
  const previous = new Date(day);
  previous.setDate(previous.getDate() - 1);
  return previous;
}

/** Figma's activity column, in the viewer's own zone: "11:04" today, "Yesterday", "29 Aug" this year, "29 Aug 2025" before. */
export function formatActivityTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return UNKNOWN;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return UNKNOWN;
  if (isSameLocalDay(at, now)) return TIME_OF_DAY.format(at);
  if (isSameLocalDay(at, previousLocalDay(now))) return 'Yesterday';
  const sameYear = at.getFullYear() === now.getFullYear();
  return (sameYear ? DAY_MONTH : DAY_MONTH_YEAR).format(at);
}

export interface BreakdownPart {
  label: string;
  count: number;
}

/** A chart's data as one sentence for screen readers, e.g. "By stage: Not contacted 4 (31%), In flight 3 (23%)." */
export function describeBreakdown(title: string, parts: readonly BreakdownPart[]): string {
  if (parts.every((part) => part.count === 0)) return `${title}: no creators yet.`;
  const percents = splitPercents(parts.map((part) => part.count));
  const items = parts.map((part, index) => `${part.label} ${part.count} (${formatSplitPercent(percents[index] ?? 0, part.count)})`);
  return `${title}: ${items.join(', ')}.`;
}

const QUOTED_LABEL = 'Quoted';
/** Figma's word for an empty Rates on file cell. */
const NO_RATES_LABEL = 'None';

/** The Creators list's Rates on file: "2 locked", "Quoted" while only a quote is on file, else "None". */
export function ratesOnFileLabel(onFile: CrmRatesOnFile): string {
  if (onFile.kind === 'locked') return `${onFile.count} locked`;
  if (onFile.kind === 'quoted') return QUOTED_LABEL;
  return NO_RATES_LABEL;
}
